import { Prescription } from "./Prescription";
import { Block } from "./Block";
import { WarmupProtocol } from "./WarmupProtocol";

// Define Action Types
export enum ActionType {
  EXERCISE = "exercise",
  DIET = "diet",
  MEDITATION = "meditation",
  SLEEP = "sleep",
  CUSTOM = "custom",
  // Subaction-only type. A "prompt" subaction is chat-only: it carries no
  // workout/diet execution fields and reuses `reflectionPrompt` as its content,
  // opening the assistant chat directly in the app.
  PROMPT = "prompt",
}

// Define Subaction Schema
export type Subaction = {
  id: string; // Unique identifier for each subaction.
  subactionId?: string; // Reference to the original library Subaction doc when the embedded instance was deduped against the library.
  name: string; // Name of the subaction (e.g., Push-ups).
  description: string; // Detailed description of the subaction.
  habitId?: string; // Optional reference to the parent habit.
  actionId?: string; // Optional reference to the parent action.
  phase?: string;
  primaryIntent?: string;
  secondaryBenefits?: string;
  bodyRegions?: string[];
  movementQualities?: string[];
  compensationsAddressed?: string[];
  userUseCases?: string[];
  difficultyLevel?: string;
  loadType?: string;
  duration?: number; // Duration of the subaction in minutes (if applicable).
  repetitions?: number; // Legacy scalar — number of repetitions. Superseded by per-set `prescription.sets[i].reps` when `prescription` is set.
  sets?: number; // Legacy scalar — number of working sets. Superseded by `prescription.sets` (working sets are the entries without `percent`) when `prescription` is set.
  caloriesBurned?: number; // Estimated calories burned (if applicable).
  intensityLevel?: string; // Intensity of the subaction (e.g., low, medium, high).
  equipmentNeeded?: string[]; // List of equipment needed.
  location?: string; // Location where the exercise should be performed.
  restTime?: number; // Rest time in seconds between sets.
  backgroundImageURL?: string;
  videoURL?: string;
  videoFileName?: string; // Original file name for an uploaded subaction video.
  instructionsURL?: string; // External link to written/visual instructions.
  quantity?: string;
  notes?: string;
  reflectionPrompt?: string; // System prompt used by the assistant chat inside this subaction.
  introMessages?: string[]; // Authored opening chat bubbles shown instantly when the chat opens, before any model call. Used by prompt subactions.
  suggestedReplies?: string[]; // Tappable starter answers shown under the intro to help the user begin the conversation.
  regulation?: RegulationPracticeMetadata;
  tags?: any[];
  ingredients?: Ingredient[];
  actionType: ActionType; // Type of the subaction.
  createdAt: string; // Timestamp when the subaction was created.
  updatedAt?: string; // Timestamp for the last update of the subaction.
  isCustom?: any;
  communityId?: string;
  // New per-set schema. When present, this is authoritative; consumers should
  // read reps, rpe, tempo, rest, etc. from here instead of the legacy scalars
  // (`sets`, `repetitions`, `restTime`). Both shapes remain valid on the type
  // so legacy data keeps working — use `getWorkingSetCount` / `getWorkingSets`
  // below to read a subaction's set count without branching at every call site.
  prescription?: Prescription;
  alternatives?: ExerciseAlternative[];
};

// Returns the working-set entries (warmups excluded) when a prescription is
// present, otherwise null — callers fall back to `subaction.sets` for the
// legacy shape.
export const getWorkingSets = (subaction: Pick<Subaction, "prescription">) => {
  const sets = subaction.prescription?.sets;
  if (!sets?.length) return null;
  return sets.filter((s) => s.percent == null);
};

// Effective working-set count for either shape. Prefer this over reading
// `subaction.sets` directly when you need to count rows, render progress,
// or size a tracking array.
export const getWorkingSetCount = (
  subaction: Pick<Subaction, "sets" | "prescription">
): number => {
  return getWorkingSets(subaction)?.length ?? subaction.sets ?? 1;
};

// Total number of "steps" the player iterates through for this subaction:
// every prescription entry (warmups + working) when prescription is set, or
// the legacy scalar otherwise. Use this for the workout cursor that walks
// through each set in order.
export const getTotalSetSteps = (
  subaction: Pick<Subaction, "sets" | "prescription">
): number => {
  return subaction.prescription?.sets?.length ?? subaction.sets ?? 1;
};

export type ExerciseAlternative = {
  name: string;
  subactionId?: string;
};

// Extend Action Schema to Include Subactions
export type Action = {
  id: string; // Unique identifier for each action.
  name: string; // Name of the action (e.g., 30-minute workout).
  description: string; // Detailed description of the action.
  habitId: string;
  videoURL?: string;
  linkedActionPlanIds?: string[];
  duration?: number; // Total duration of the action in minutes (if applicable).
  goal?: string; // Specific goal related to the action.
  actionType: ActionType; // Type of the action.
  subactions?: Subaction[]; // List of subactions associated with the action.
  blocks?: Block[];
  warmupProtocol?: WarmupProtocol;
  restDayAfter?: "mandatory" | "optional" | "none";
  backgroundImageURL?: string;
  tags?: any[];
  createdAt: string; // Timestamp when the action was created.
  updatedAt?: string; // Timestamp for the last update of the action.
  // Set by the workout-program importer so HabitDetailScreen can filter out
  // plan-owned actions from the habit's standalone action list.
  planID?: string;
};

// Define Action Tracking
export type ActionTracking = {
  id: string; // Unique identifier for each action tracking instance.
  actionId: string; // Reference to the Action.
  date: string; // The date when the user performed the action.
  progressTracking?: {
    weight?: number; // Weight lifted (if applicable).
    distance?: number; // Distance covered (if applicable).
    repetitions?: number; // Repetitions performed (if applicable).
    sets?: number; // Sets performed (if applicable).
  };
  habitId: string;
  title?: string;
  subactionIds: string[];
  feedback?: string; // Field for feedback or notes.
  notes?: string; // Any additional notes the user might want to add for that particular instance.
  regulation?: RegulationSessionResult;
};

export type RegulationState =
  | "wired"
  | "tense"
  | "scattered"
  | "flat"
  | "practice";

export type RegulationEffect =
  | "settle"
  | "release"
  | "orient"
  | "mobilize";

export type RegulationOutcome = "helped" | "same" | "more_activated";

export type RegulationPracticeMetadata = {
  startingStates?: RegulationState[];
  intendedEffects?: RegulationEffect[];
  durationSeconds?: number;
  setting?: Array<"anywhere" | "private" | "outdoors">;
  posture?: Array<"seated" | "standing" | "lying">;
  sensoryChannels?: string[];
  cautions?: string[];
};

export type RegulationSessionResult = {
  subactionId: string;
  startingState: RegulationState;
  outcome: RegulationOutcome;
  durationSeconds: number;
  requestedDurationMinutes: number;
};

export type Ingredient = {
  name: string;
  quantity: number;
  measurementSize: string;
};
